/**
 * Browser tasks' pure logic (AMO-155): the editor's form and how it becomes
 * a task definition (with the same limits the server keeps), the sites list
 * as typed, and which tasks a workflow's Browser task step may choose.
 *
 * @module components/workflows/BrowserTasks.logic
 */
import {
  BROWSER_TASK_LIMITS,
  type BrowserTask,
  type BrowserTaskDefinition,
  type BrowserTaskScope,
  type BrowserTaskStatus,
  type BrowserTaskSummary,
} from "@amoreg/contracts";

/** What the editor holds: the definition's fields as typed, sites one per line. */
export interface BrowserTaskForm {
  readonly name: string;
  readonly purpose: string;
  readonly startUrl: string;
  readonly steps: string;
  readonly successCheck: string;
  readonly produces: string;
  readonly allowedDomains: string;
  readonly scope: BrowserTaskScope;
}

export const emptyBrowserTaskForm = (): BrowserTaskForm => ({
  name: "",
  purpose: "",
  startUrl: "https://",
  steps: "",
  successCheck: "",
  produces: "",
  allowedDomains: "",
  scope: "personal",
});

export const browserTaskFormFrom = (task: Pick<BrowserTask, "definition" | "scope">): BrowserTaskForm => ({
  name: task.definition.name,
  purpose: task.definition.purpose,
  startUrl: task.definition.startUrl,
  steps: task.definition.steps,
  successCheck: task.definition.successCheck,
  produces: task.definition.produces,
  allowedDomains: task.definition.allowedDomains.join("\n"),
  scope: task.scope,
});

/** The contract's own pattern: a host name, or `*.` and a host name. */
const DOMAIN = /^(\*\.)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;

/**
 * The sites as typed, one per line (commas and spaces split too), lower-cased
 * and without repeats. A pasted address keeps only its host:
 * `https://www.fca.org.uk/news` is `www.fca.org.uk`.
 */
export function parseAllowedDomains(text: string): ReadonlyArray<string> {
  const seen = new Set<string>();
  for (const raw of text.split(/[\s,]+/)) {
    const host = raw
      .trim()
      .toLowerCase()
      .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
      .replace(/[/?#].*$/, "")
      .replace(/:\d+$/, "")
      .replace(/\.$/, "");
    if (host) seen.add(host);
  }
  return [...seen];
}

/** The start page's host, for "Sites it may visit" to suggest; null until it is an https address. */
export function startUrlHost(startUrl: string): string | null {
  if (!/^https:\/\//i.test(startUrl.trim())) return null;
  try {
    const host = new URL(startUrl.trim()).hostname.toLowerCase();
    return DOMAIN.test(host) ? host : null;
  } catch {
    return null;
  }
}

/** Whether `host` is one of the sites, a `*.` entry covering its subdomains included. */
export function domainCovers(domains: ReadonlyArray<string>, host: string): boolean {
  return domains.some((domain) =>
    domain.startsWith("*.") ? host === domain.slice(2) || host.endsWith(domain.slice(1)) : host === domain,
  );
}

export type BrowserTaskFormCheck =
  | { readonly ok: true; readonly definition: BrowserTaskDefinition }
  | { readonly ok: false; readonly error: string };

/** The form as a definition the server takes, or the first thing to fix, in plain words. */
export function checkBrowserTaskForm(form: BrowserTaskForm): BrowserTaskFormCheck {
  const limits = BROWSER_TASK_LIMITS;
  const name = form.name.trim();
  // The server takes https:// in lower case only; HTTPS:// is the same address.
  const startUrl = form.startUrl.trim().replace(/^https:\/\//i, "https://");
  const steps = form.steps.trim();
  const domains = parseAllowedDomains(form.allowedDomains);
  if (!name) return { ok: false, error: "Give the task a name." };
  if (name.length > limits.nameLength) return { ok: false, error: `Keep the name under ${limits.nameLength} characters.` };
  if (form.purpose.length > limits.purposeLength) return { ok: false, error: "Shorten what it is for." };
  if (startUrlHost(startUrl) === null) return { ok: false, error: "The start page must be a full address beginning https://." };
  if (startUrl.length > limits.startUrlLength) return { ok: false, error: "The start page address is too long." };
  if (!steps) return { ok: false, error: "Write the steps it takes." };
  if (steps.length > limits.stepsLength) return { ok: false, error: "Shorten the steps." };
  if (form.successCheck.length > limits.successCheckLength) return { ok: false, error: "Shorten how to tell it worked." };
  if (form.produces.length > limits.producesLength) return { ok: false, error: "Shorten the files it produces." };
  if (domains.length === 0) return { ok: false, error: "Add at least one site it may visit." };
  if (domains.length > limits.domains) return { ok: false, error: `List at most ${limits.domains} sites.` };
  const bad = domains.find((domain) => domain.length > limits.domainLength || !DOMAIN.test(domain));
  if (bad) return { ok: false, error: `“${bad}” is not a site name. Write it like fca.org.uk or *.gov.uk.` };
  return {
    ok: true,
    definition: {
      name,
      purpose: form.purpose.trim(),
      startUrl,
      steps,
      successCheck: form.successCheck.trim(),
      produces: form.produces.trim(),
      allowedDomains: domains,
    },
  };
}

/** Whether saving the form would change the task. */
export function browserTaskFormDiffers(form: BrowserTaskForm, task: Pick<BrowserTask, "definition" | "scope">): boolean {
  if (form.scope !== task.scope) return true;
  const check = checkBrowserTaskForm(form);
  if (!check.ok) return true;
  return JSON.stringify(check.definition) !== JSON.stringify(task.definition);
}

export const SCOPE_LABEL: Readonly<Record<BrowserTaskScope, string>> = {
  personal: "Personal",
  organization: "Organisation",
};

export const STATUS_LABEL: Readonly<Record<BrowserTaskStatus, string>> = {
  draft: "Draft",
  active: "Ready",
};

export interface BrowserTaskChoice {
  readonly id: string;
  readonly name: string;
  readonly scope: BrowserTaskScope;
  /** Why a step cannot use it yet; null when it can. */
  readonly unavailable: string | null;
}

/**
 * The tasks a Browser task step can choose, ready ones first, each kind in
 * name order; a draft is listed but unavailable until its owner reviews it.
 */
export function browserTaskChoices(tasks: ReadonlyArray<BrowserTaskSummary>): ReadonlyArray<BrowserTaskChoice> {
  return [...tasks]
    .sort(
      (left, right) =>
        Number(left.status === "draft") - Number(right.status === "draft") ||
        left.name.localeCompare(right.name, undefined, { sensitivity: "base" }),
    )
    .map((task) => ({
      id: task.id,
      name: task.name,
      scope: task.scope,
      unavailable: task.status === "draft" ? "Draft: review it first" : null,
    }));
}

/** Each task's name by id, for the chart's step cards. */
export const browserTaskNames = (tasks: ReadonlyArray<Pick<BrowserTaskSummary, "id" | "name">>): ReadonlyMap<string, string> =>
  new Map(tasks.map((task) => [task.id, task.name]));
